import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

// MUI Components
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import TextField from "@material-ui/core/TextField";
import { Typography } from "@material-ui/core";

// Other Files
import { customTheme } from "../../constants/customTheme";
import classNames from "classnames";
import VirtualKeyboard from "../Shared/VirtualKeyboard";

import {
  saveCustomProtocol,
  toggleProtocolDialog,
  errorResponse,
  setVirtualKeyboard,
} from "../../actions";

const useStyles = makeStyles({
  root: (props) => customTheme(props.theme).dialog.root,
  buttonContainer: customTheme().dialog.buttonContainer,
  buttonPrimary: (props) => customTheme(props.theme).buttonPrimary,
  buttonOutlined: (props) => customTheme(props.theme).buttonOutlined,
  title: customTheme().dialog.title,
  contentContainer: (props) => ({
    ...customTheme().dialog.contentContainer,
    ...customTheme(props.theme).customScrollBar,
    overflowY: "auto",
  }),
  contentItem: customTheme().dialog.contentItem,
});

const SaveProtocolDialog = (props) => {
  const classes = useStyles(props);

  const [protocolName, setProtocolName] = React.useState("");
  const [protocolId, setProtocolId] = React.useState("");
  const [keyboardInput, setKeyboardInput] = React.useState({
    inputName: "protocolName",
    inputUpdate: { protocolName: "" },
  });

  const handleFormChange = () => (event) => {
    let newName = event.target ? event.target.value : event;

    const id =
      "CUSTOM_" +
      newName
        .replace(/[^a-zA-Z _]/g, "")
        .replace(/ /g, "_")
        .toUpperCase(); //remove special characters (anything thats not a letter), replace spaces with underscore, turn all letters upper case
    setProtocolName(newName);
    setProtocolId(id);
    handleInputCheck();

    if (event.target) {
      setKeyboardInput({
        ...keyboardInput,
        inputUpdate: { protocolName: event.target.value },
      });
    }
  };

  const handleInputCheck = () => {
    let error = { state: false, message: "" };
    const protocolNames = [];
    const protocolIds = [];
    for (let id of Object.keys(props.protocols)) {
      protocolIds.push(id);
      protocolNames.push(props.protocols[id].name);
    }
    if (protocolIds.includes(protocolId)) {
      error = {
        state: true,
        message: "Protocol ID is taken. Enter a different name",
      };
    } else if (protocolNames.includes(protocolName)) {
      error = {
        state: true,
        message: "Protocol name is taken. Enter a different name",
      };
    }
    return error;
  };

  const handleSave = () => {
    let result = handleInputCheck();
    if (result.state === true) {
      props.errorResponse(result.message);
    } else {
      props.saveCustomProtocol({
        protocolId: protocolId,
        protocolName: protocolName,
      });
      props.onClose();
    }
    props.setVirtualKeyboard(false);
  };

  return (
    <Dialog
      open={props.open}
      onExiting={() => props.setVirtualKeyboard(false)}
      PaperProps={{ className: classNames(classes.root) }}
    >
      <Typography
        className={classes.title}
        variant="h4"
        color="textPrimary"
        align="center"
      >
        <span>Save a Custom Protocol</span>
      </Typography>
      <DialogContent className={classes.contentContainer}>
        <Typography
          className={classes.contentItem}
          color="textPrimary"
          align="center"
        >
          Any location additions or deletions you have made will be stored and
          saved to a protocol stored locally. You will be able to load this
          protocol at the start of any subsequent studies.
        </Typography>
        <TextField
          className={classes.contentItem}
          fullWidth
          label="Name Your Custom Protocol"
          value={protocolName}
          onFocus={() => {
            props.setVirtualKeyboard(true);
          }}
          onChange={handleFormChange()}
        />
      </DialogContent>

      <DialogActions className={classes.buttonContainer}>
        <Button
          className={classes.buttonOutlined}
          onClick={props.onClose}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          className={classes.buttonPrimary}
          onClick={handleSave}
          variant="contained"
          color="primary"
        >
          Save Protocol
        </Button>
      </DialogActions>
      <VirtualKeyboard
        anchor={"center"}
        keyboardInitialInput={{ protocolName: protocolName }}
        keyboardInputUpdate={keyboardInput.inputUpdate}
        keyboardInputName={keyboardInput.inputName}
        keyboardChange={handleFormChange()}
      />
    </Dialog>
  );
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      saveCustomProtocol,
      toggleProtocolDialog,
      errorResponse,
      setVirtualKeyboard,
    },
    dispatch
  );
};

const mapStateToProps = (state) => {
  return {
    protocols: state.availableProtocols.protocolsById,
    theme: state.utility.theme,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SaveProtocolDialog);
